import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccountBalance as GstIcon,
  Groups as GroupsIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useParams, useNavigate } from 'react-router-dom';
import { request } from '../../../../../utils/request';
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import { Party } from '../../../types';
import { usePermissions } from '../../../../../utils/usePermissions';



const PartyDetails = () => {
  const { canUpdateParties } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [party, setParty] = useState<Party | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    const fetchParty = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await request({
          url: `/party/get-party/${id}`,
          method: 'get',
        });

        if (response.party) {
          const partyData = response.party;
          setParty(partyData);
        } else {
          setError('Party not found');
        }
      } catch (error: any) {
        console.error('Failed to fetch party:', error);
        setError(error?.message || 'Failed to load party details');
        toast.error('Failed to load party details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchParty();
    }
  }, [id]);

  const handleEdit = () => {
    if (party) {
      navigate(`/parties/editParty/${party._id}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading party details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/parties')}
        >
          Back to Parties
        </Button>
      </Box>
    );
  }

  if (!party) {
    return (
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
        <Alert severity="warning">
          Party not found
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/parties')}
          sx={{ mt: 2 }}
        >
          Back to Parties
        </Button>
      </Box>
    );
  }

  // Responsive layout helper
  const renderTwoColumnLayout = (leftContent: React.ReactNode, rightContent: React.ReactNode) => {
    if (isMobile || isTablet) {
      return (
        <Stack spacing={2}>
          {leftContent}
          {rightContent}
        </Stack>
      );
    }

    return (
      <Box display="flex" gap={2} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {leftContent}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {rightContent}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Brandcrumb />
        <Chip
          onClick={() => navigate(-1)}
          icon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />}
          label="Back"
          variant="filled"
          color="secondary"
          sx={{
            fontSize: '14px',
            color: 'text.secondary',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        />
      </Box>

      {/* Header Card - Compact */}
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2.5}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50, flexShrink: 0 }}>
                <GroupsIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  fontWeight="bold"
                  noWrap
                  sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                >
                  {party.partyName}
                </Typography>
                {party.nickName && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {party.nickName}
                  </Typography>
                )}
                <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  <Chip
                    label={party.status}
                    color={party.status === 'active' ? 'success' : 'error'}
                    size="small"
                    sx={{ height: 24 }}
                  />
                  <Chip
                    label={`GST: ${party.withGST ? "Yes" : "No"}`}
                    color='secondary'
                    size="small"
                    sx={{ height: 24 }}
                  />
                  <Chip
                    label={party.entityCategory.replace('_', ' ')}
                    color='primary'
                    size="small"
                    sx={{ height: 24 }}
                  />
                </Box>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
              <Tooltip title="Check Return and Replacement" placement='top' arrow>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate('/productReturn', {
                    state: {
                      partyId: party._id,
                      source: 'party'
                    }
                  })}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  Check R&R
                </Button>
              </Tooltip>

              <Tooltip title="Check Enquiry" placement='top' arrow>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate('/enquiry', {
                    state: {
                      partyId: party._id,
                      source: 'party'
                    }
                  })}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  Check Enquiry
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                color='secondary'
                size='small'
                onClick={() => navigate(`/parties/dailyLedger/${party._id}`)}
                sx={{ minWidth: 120 }}
              >
                Daily Ledger
              </Button>
              {canUpdateParties() && (
                <Tooltip title="Edit Party">
                  <IconButton
                    color="primary"
                    onClick={handleEdit}
                    size="small"
                  >
                    <EditIcon fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </Tooltip>
              )}

            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Main Content - Two Column Layout */}
      {renderTwoColumnLayout(
        /* Left Column */
        <Stack spacing={1.5}>
          {/* Basic Information Card */}
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Basic Information
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              <Stack spacing={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
                  <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 60 }}>
                    Location:
                  </Typography>
                  <Typography variant="body2" noWrap>{party.location}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <CategoryIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
                  <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 60 }}>
                    Category:
                  </Typography>
                  <Chip
                    label={party.entityCategory.replace('_', ' ')}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </Box>
                {party.gstNumber && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <GstIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
                    <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 60 }}>
                      GST NO:
                    </Typography>
                    <Typography variant="body2" noWrap>{party.gstNumber}</Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Contact Information Card */}
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Contact Information
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              <Stack spacing={1.5}>
                {/* Phone Numbers */}
                <Box>
                  <Typography variant="caption" fontWeight="bold" gutterBottom>
                    Phone Numbers
                  </Typography>
                  <Stack spacing={0.75}>
                    {party.contact.phone.map((phone, index) => (
                      <Box key={index} display="flex" alignItems="center" gap={1}>
                        <PhoneIcon sx={{ color: 'text.secondary', fontSize: 16, flexShrink: 0 }} />
                        <Chip
                          label={phone.label}
                          size="small"
                          variant="outlined"
                          sx={{ minWidth: 50, height: 20, fontSize: '0.7rem' }}
                        />
                        <Typography variant="body2" fontFamily="monospace" noWrap>
                          {phone.phoneNo}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                {/* Email Addresses */}
                <Box>
                  <Typography variant="caption" fontWeight="bold" gutterBottom>
                    Email Addresses
                  </Typography>
                  <Stack spacing={0.75}>
                    {party.contact.email.map((email, index) => (
                      <Box key={index} display="flex" alignItems="center" gap={1}>
                        <EmailIcon sx={{ color: 'text.secondary', fontSize: 16, flexShrink: 0 }} />
                        <Typography variant="body2" noWrap>{email}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>,

        /* Right Column */
        <Stack spacing={1.5}>
          {/* Address Information Card */}
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Address
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              <Stack spacing={1}>
                {party?.address?.line1 && (
                  <Typography variant="body2">{party.address.line1}</Typography>
                )}
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {party?.address?.city && (
                    <Chip label={party.address.city} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                  {party?.address?.state && (
                    <Chip label={party.address.state} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                  {party?.address?.country && (
                    <Chip label={party.address.country} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                  {party?.address?.pinCode && (
                    <Chip label={party.address.pinCode} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Additional Information Card */}
          <Card elevation={1}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Additional Information
              </Typography>
              <Divider sx={{ mb: 1.5 }} />

              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Created:
                  </Typography>
                  <Typography variant="caption" fontWeight={500}>
                    {new Date(party.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Last Updated:
                  </Typography>
                  <Typography variant="caption" fontWeight={500}>
                    {new Date(party.updatedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}
    </>
  );
};

export default PartyDetails;